'use client';

import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Mail, Phone } from 'lucide-react';

export default function Footer() {
  return (
    <footer style={{ backgroundColor: '#080808', borderTop: '1px solid #2a2a2a' }} className="py-8">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Brand + Store Info */}
          <div className="col-span-1 md:col-span-2 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <AppLogo size={40} />
              <span className="font-extrabold text-xl" style={{ color: '#f5f0e8' }}>LP Refurbished</span>
            </div>
            <div className="flex flex-col gap-4">
              <div>
                <h4 className="text-[11px] font-bold uppercase tracking-widest mb-1" style={{ color: '#5a4a35' }}>Visit Our Store</h4>
                <p className="text-sm font-semibold leading-snug" style={{ color: '#f5f0e8' }}>
                  Australia-wide Mail-In Service<br />
                  <span style={{ color: '#8a7a65', fontWeight: 400 }}>Serving customers across Australia</span>
                </p>
              </div>
              <div>
                <h4 className="text-[11px] font-bold uppercase tracking-widest mb-1" style={{ color: '#5a4a35' }}>Opening Hours</h4>
                <div className="flex flex-col gap-0.5 text-sm">
                  <div className="flex justify-between gap-6">
                    <span className="font-semibold" style={{ color: '#f5f0e8' }}>Mon – Sun</span>
                    <span className="font-semibold" style={{ color: '#8a7a65' }}>8:00 AM – 12:00 AM</span>
                  </div>
                </div>
              </div>
              <a
                href="tel:0481446276"
                className="font-bold text-base transition-colors"
                style={{ color: '#d4a843' }}
              >
                0481 446 276
              </a>
            </div>
            {/* Social links */}
            <div className="flex items-center gap-3">
              <a
                href="mailto:lukapemberton@gmail.com"
                className="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
                style={{ backgroundColor: '#1a1a1a', color: '#8a7a65' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(212,168,67,0.15)'; (e.currentTarget as HTMLElement).style.color = '#d4a843'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = '#1a1a1a'; (e.currentTarget as HTMLElement).style.color = '#8a7a65'; }}
                aria-label="Email LP Refurbished"
              >
                <Mail size={16} />
              </a>
              <a
                href="tel:0481446276"
                className="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
                style={{ backgroundColor: '#1a1a1a', color: '#8a7a65' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(212,168,67,0.15)'; (e.currentTarget as HTMLElement).style.color = '#d4a843'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = '#1a1a1a'; (e.currentTarget as HTMLElement).style.color = '#8a7a65'; }}
                aria-label="Call LP Refurbished"
              >
                <Phone size={16} />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-[11px] font-bold uppercase tracking-widest mb-4" style={{ color: '#5a4a35' }}>Services</h4>
            <ul className="flex flex-col gap-2.5">
              {[
                { label: 'Phone Repairs', href: '/repairs' },
                { label: 'Refurbished Phones', href: '/refurbished-phones' },
                { label: 'Mail-In Repairs', href: '/repairs' },
                { label: 'Get a Quote', href: '/repairs' },
              ]?.map((item) => (
                <li key={`footer-service-${item?.label}`}>
                  <Link
                    href={item?.href}
                    className="text-sm font-medium transition-colors"
                    style={{ color: '#8a7a65' }}
                    onMouseEnter={e => ((e.currentTarget as HTMLElement).style.color = '#d4a843')}
                    onMouseLeave={e => ((e.currentTarget as HTMLElement).style.color = '#8a7a65')}
                  >
                    {item?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* LP Refurbished */}
          <div>
            <h4 className="text-[11px] font-bold uppercase tracking-widest mb-4" style={{ color: '#5a4a35' }}>LP Refurbished</h4>
            <ul className="flex flex-col gap-2.5">
              {[
                { label: 'Home', href: '/' },
                { label: 'Repairs', href: '/repairs' },
                { label: 'Refurbished Phones', href: '/refurbished-phones' },
                { label: 'About Us', href: '/about' },
                { label: 'Referral & Loyalty', href: '/about#referral' },
                { label: 'FAQ', href: '/faq' },
                { label: 'Warranty & Returns', href: '/warranty' },
                { label: 'Contact Us', href: '/contact' },
                { label: 'Repair Terms', href: '/repairs/terms' },
              ]?.map((item) => (
                <li key={`footer-link-${item?.label}`}>
                  <Link
                    href={item?.href}
                    className="text-sm font-medium transition-colors"
                    style={{ color: '#8a7a65' }}
                    onMouseEnter={e => ((e.currentTarget as HTMLElement).style.color = '#d4a843')}
                    onMouseLeave={e => ((e.currentTarget as HTMLElement).style.color = '#8a7a65')}
                  >
                    {item?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-6" style={{ borderTop: '1px solid #2a2a2a' }}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <p className="text-xs" style={{ color: '#5a4a35' }}>
              ABN: 90 220 655 876
            </p>
            <div className="flex items-center gap-4">
              <p className="text-xs" style={{ color: '#5a4a35' }}>© 2026 LP Refurbished Pty Ltd</p>
              <div className="flex items-center gap-3">
                <Link href="/repairs" className="text-xs transition-colors" style={{ color: '#5a4a35' }}
                  onMouseEnter={e => ((e.currentTarget as HTMLElement).style.color = '#d4a843')}
                  onMouseLeave={e => ((e.currentTarget as HTMLElement).style.color = '#5a4a35')}
                >Terms</Link>
                <Link href="/warranty" className="text-xs transition-colors" style={{ color: '#5a4a35' }}
                  onMouseEnter={e => ((e.currentTarget as HTMLElement).style.color = '#d4a843')}
                  onMouseLeave={e => ((e.currentTarget as HTMLElement).style.color = '#5a4a35')}
                >Warranty</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}